/**
 * The discography page's actions menu: the secondary toolbar CTAs (Bulk
 * Edit, Validate, Reset Sort, Reset Filters) behind one burger button. The
 * main CTA (Create Recording) and the view controls (Expand/Collapse All,
 * Show/Hide Data) stay on the toolbar to its left.
 *
 * The trigger is a hand-rolled `<button>` rather than a `WButton`: `WButton`
 * renders a `<div onClick>` with no `role`, `tabIndex`, keyboard handling or
 * `aria-label` prop, so it cannot carry an icon-only control accessibly. The
 * classes reproduce `WButton`'s `primary` variant so the two read as the same
 * control family, and the divider-separated group keeps the menu physically
 * apart from the view/filter utilities on its left.
 */
import React from 'react';
import { ArrowUpDown, FilterX, ListChecks, Menu, Pencil } from 'lucide-react';
import WDropdown from 'shared_components/components/WDropdownContent/WDropdown';

export type DiscographyActionsMenuItem = {
  label: string;
  icon: 'edit' | 'sort' | 'filter' | 'select';
  onClick: () => void;
};

const MENU_ICONS: Record<DiscographyActionsMenuItem['icon'], React.ReactNode> =
  {
    edit: <Pencil className="tw-w-4 tw-h-4" />,
    sort: <ArrowUpDown className="tw-w-4 tw-h-4" />,
    filter: <FilterX className="tw-w-4 tw-h-4" />,
    select: <ListChecks className="tw-w-4 tw-h-4" />,
  };

export const DiscographyActionsMenu: React.FC<{
  items: DiscographyActionsMenuItem[];
}> = ({ items }) => (
  <div className="tw-flex tw-items-center tw-border-l tw-border-formInputBackground tw-pl-3 tw-ml-1">
    <WDropdown
      closeOnContentClick
      trigger={
        <button
          type="button"
          aria-label="Discography actions"
          title="Actions"
          className="tw-flex tw-items-center tw-justify-center tw-h-[32px] tw-w-[32px] tw-rounded-3xl tw-bg-surfaceLime tw-text-surfaceBlack90 tw-cursor-pointer focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white"
        >
          <Menu className="tw-w-4 tw-h-4" strokeWidth={2.5} />
        </button>
      }
      content={
        <div className="tw-min-w-[220px] tw-py-1">
          {items.map((item) => (
            <div
              key={item.label}
              role="button"
              tabIndex={0}
              className="tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-3 tw-text-sm tw-whitespace-nowrap tw-text-white tw-cursor-pointer hover:tw-bg-neutral-800"
              onClick={item.onClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  item.onClick();
                }
              }}
            >
              <span className="tw-text-neutral-400">
                {MENU_ICONS[item.icon]}
              </span>
              {item.label}
            </div>
          ))}
        </div>
      }
    />
  </div>
);
